import { type ContactRequestPayload, ContactSubject, contactRequestSchema } from "@groupeo/shared";
import { zodResolver } from "@hookform/resolvers/zod";
import { Mail, MessageSquareText, ShieldCheck } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { BreadcrumbJsonLd } from "@/src/common/components/breadcrumb-jsonld";
import { JsonLd } from "@/src/common/components/json-ld";
import { PageHelmet } from "@/src/common/components/page-helmet";
import { env } from "@/src/common/constants/env";
import { DocumentPageShell } from "../components/document-page-shell";
import { MARKETING_ROUTES } from "../constants/marketing-routes";

const contactFormSchema = contactRequestSchema.omit({ captcha_token: true });

type ContactFormValues = Omit<ContactRequestPayload, "captcha_token">;

interface Grecaptcha {
	ready: (callback: () => void) => void;
	render: (
		container: HTMLElement,
		parameters: {
			sitekey: string;
			size: "invisible";
			badge: "bottomright";
			callback: (token: string) => void;
			"error-callback": () => void;
			"expired-callback": () => void;
		},
	) => number;
	execute: (widgetId: number) => void;
	reset: (widgetId: number) => void;
}

declare global {
	interface Window {
		grecaptcha?: Grecaptcha;
		onLookDatRecaptchaLoad?: () => void;
	}
}

function loadRecaptchaScript() {
	return new Promise<void>((resolve, reject) => {
		if (window.grecaptcha) {
			resolve();
			return;
		}

		const existingScript = document.querySelector<HTMLScriptElement>('script[data-lookdat-recaptcha="true"]');

		if (existingScript) {
			existingScript.addEventListener("load", () => resolve(), { once: true });
			existingScript.addEventListener("error", () => reject(new Error("recaptcha_load_failed")), { once: true });
			return;
		}

		window.onLookDatRecaptchaLoad = () => resolve();

		const script = document.createElement("script");
		script.src = "https://www.google.com/recaptcha/api.js?onload=onLookDatRecaptchaLoad&render=explicit";
		script.async = true;
		script.defer = true;
		script.dataset.lookdatRecaptcha = "true";
		script.onerror = () => reject(new Error("recaptcha_load_failed"));

		document.head.append(script);
	});
}

function getErrorMessage(errorCode: string | null) {
	switch (errorCode) {
		case "invalid_captcha":
			return "La vérification anti-spam a échoué. Merci de réessayer.";
		case "failed_to_send_contact_message":
			return "Le message n'a pas pu être envoyé pour le moment. Merci de réessayer un peu plus tard.";
		default:
			return "Une erreur est survenue. Merci de réessayer.";
	}
}

export default function ContactPage() {
	const { t } = useTranslation("marketing");
	const [feedback, setFeedback] = useState<{ type: "success" | "error"; message: string } | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const captchaContainerRef = useRef<HTMLDivElement | null>(null);
	const captchaWidgetIdRef = useRef<number | null>(null);
	const pendingValuesRef = useRef<ContactFormValues | null>(null);

	const form = useForm<ContactFormValues>({
		resolver: zodResolver(contactFormSchema),
		defaultValues: {
			email: "",
			subject: ContactSubject.OTHER,
			message: "",
		},
	});

	const sendMessage = useCallback(
		async (values: ContactFormValues, captchaToken: string) => {
			try {
				const response = await fetch(`${env.SERVER_URL}/contact`, {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
					},
					body: JSON.stringify({
						...values,
						captcha_token: captchaToken,
					} satisfies ContactRequestPayload),
				});

				if (!response.ok) {
					const data = (await response.json().catch(() => null)) as { error?: string } | null;
					throw new Error(data?.error ?? "contact_error");
				}

				form.reset({
					email: "",
					subject: ContactSubject.OTHER,
					message: "",
				});
				setFeedback({
					type: "success",
					message: "Ton message a bien été envoyé. Nous reviendrons vers toi dès que possible.",
				});
			} catch (error) {
				setFeedback({
					type: "error",
					message: getErrorMessage(error instanceof Error ? error.message : null),
				});
			} finally {
				if (captchaWidgetIdRef.current !== null && window.grecaptcha) {
					window.grecaptcha.reset(captchaWidgetIdRef.current);
				}
				pendingValuesRef.current = null;
				setIsSubmitting(false);
			}
		},
		[form],
	);

	useEffect(() => {
		if (!env.RECAPTCHA_SITE_KEY) {
			return;
		}

		let cancelled = false;

		void loadRecaptchaScript()
			.then(() => {
				if (cancelled || !window.grecaptcha || !captchaContainerRef.current || captchaWidgetIdRef.current !== null) {
					return;
				}

				window.grecaptcha.ready(() => {
					if (cancelled || !window.grecaptcha || !captchaContainerRef.current || captchaWidgetIdRef.current !== null) {
						return;
					}

					captchaWidgetIdRef.current = window.grecaptcha.render(captchaContainerRef.current, {
						sitekey: env.RECAPTCHA_SITE_KEY,
						size: "invisible",
						badge: "bottomright",
						callback: (token) => {
							const values = pendingValuesRef.current;
							if (!values) {
								return;
							}
							void sendMessage(values, token);
						},
						"error-callback": () => {
							setFeedback({
								type: "error",
								message: "La vérification anti-spam a échoué. Merci de réessayer.",
							});
							setIsSubmitting(false);
							pendingValuesRef.current = null;
						},
						"expired-callback": () => {
							pendingValuesRef.current = null;
						},
					});
				});
			})
			.catch(() => {
				setFeedback({
					type: "error",
					message: "Le captcha n'a pas pu être chargé. Merci de réessayer plus tard.",
				});
			});

		return () => {
			cancelled = true;
		};
	}, [sendMessage]);

	const onSubmit = form.handleSubmit((values) => {
		if (!env.RECAPTCHA_SITE_KEY || captchaWidgetIdRef.current === null || !window.grecaptcha) {
			setFeedback({
				type: "error",
				message: "Le captcha Google n'est pas encore configuré sur ce site.",
			});
			return;
		}

		pendingValuesRef.current = values;
		setFeedback(null);
		setIsSubmitting(true);
		window.grecaptcha.execute(captchaWidgetIdRef.current);
	});

	const pageUrl = `${env.SITE_URL}${MARKETING_ROUTES.CONTACT}`;

	return (
		<>
			<PageHelmet
				title="Contact"
				description="Contacte l'équipe LookDat pour un bug, une demande de fonctionnalité, une suppression de compte ou toute autre question."
			/>
			<BreadcrumbJsonLd
				items={[
					{ name: t("breadcrumb_home"), url: env.SITE_URL },
					{ name: t("nav_contact"), url: pageUrl },
				]}
			/>
			<JsonLd
				data={{
					"@context": "https://schema.org",
					"@type": "ContactPage",
					name: `Contact ${env.APP_NAME}`,
					url: pageUrl,
					mainEntity: {
						"@type": "Organization",
						name: env.APP_NAME,
						contactPoint: {
							"@type": "ContactPoint",
							email: env.CONTACT_EMAIL,
							contactType: "customer support",
							availableLanguage: ["French", "English"],
						},
					},
				}}
			/>
			<DocumentPageShell
				eyebrow="Contact"
				title="Parlons de LookDat"
				description="Utilise ce formulaire pour signaler un bug, proposer une fonctionnalité, demander une suppression de compte ou nous écrire pour toute autre demande."
			>
				<section className="grid gap-6 lg:grid-cols-[0.72fr_1.28fr]">
					<aside className="space-y-4 rounded-4xl border border-white/10 bg-white/5 p-6 shadow-[0_24px_70px_-48px_rgba(0,0,0,0.78)] backdrop-blur">
						<div className="inline-flex size-12 items-center justify-center rounded-2xl bg-[linear-gradient(135deg,var(--color-app-accent),var(--color-app-accent-secondary))] text-white">
							<Mail className="size-5" />
						</div>
						<h2 className="text-2xl font-semibold tracking-[-0.04em] text-white">Une demande claire, une réponse plus rapide</h2>
						<p className="text-sm leading-7 text-white/70">
							Les demandes liées à la suppression de compte, à un bug ou à une fonctionnalité sont traitées à partir de ce formulaire.
						</p>
						<div className="rounded-3xl border border-white/10 bg-black/20 p-4">
							<p className="text-sm font-semibold text-white">Adresse de contact</p>
							<p className="mt-2 text-sm text-white/70">stephanie@lookdat.com</p>
						</div>
						<div className="rounded-3xl border border-white/10 bg-black/20 p-4">
							<div className="flex items-center gap-3">
								<ShieldCheck className="size-5 text-app-green" />
								<p className="text-sm text-white/70">Le formulaire est protégé par Google reCAPTCHA invisible pour limiter les spams.</p>
							</div>
						</div>
					</aside>

					<section className="rounded-4xl border border-white/10 bg-white/5 p-6 shadow-[0_24px_70px_-48px_rgba(0,0,0,0.78)] backdrop-blur sm:p-8">
						<form className="space-y-6" onSubmit={onSubmit}>
							<div className="space-y-2">
								<label htmlFor="contact-email" className="text-sm font-medium text-white">
									Adresse e-mail
								</label>
								<input
									id="contact-email"
									type="email"
									placeholder="ton@email.com"
									className="w-full rounded-2xl border border-white/10 bg-black/20 px-4 py-3.5 text-white outline-none transition focus:border-app-accent"
									{...form.register("email")}
								/>
								{form.formState.errors.email ? <p className="text-sm text-app-danger">{form.formState.errors.email.message}</p> : null}
							</div>

							<div className="space-y-2">
								<label htmlFor="contact-subject" className="text-sm font-medium text-white">
									Objet
								</label>
								<select
									id="contact-subject"
									className="w-full rounded-2xl border border-white/10 bg-black/20 px-4 py-3.5 text-white outline-none transition focus:border-app-accent"
									{...form.register("subject")}
								>
									<option value={ContactSubject.BUG}>Bug</option>
									<option value={ContactSubject.FEATURE}>Fonctionnalité</option>
									<option value={ContactSubject.ACCOUNT_DELETION}>Suppression de compte</option>
									<option value={ContactSubject.OTHER}>Autre</option>
								</select>
								{form.formState.errors.subject ? <p className="text-sm text-app-danger">{form.formState.errors.subject.message}</p> : null}
							</div>

							<div className="space-y-2">
								<label htmlFor="contact-message" className="flex items-center gap-2 text-sm font-medium text-white">
									<MessageSquareText className="size-4" />
									Message
								</label>
								<textarea
									id="contact-message"
									rows={8}
									placeholder="Décris ta demande avec le plus de contexte possible."
									className="w-full rounded-3xl border border-white/10 bg-black/20 px-4 py-4 text-white outline-none transition focus:border-app-accent"
									{...form.register("message")}
								/>
								{form.formState.errors.message ? <p className="text-sm text-app-danger">{form.formState.errors.message.message}</p> : null}
							</div>

							<div ref={captchaContainerRef} />

							{feedback ? (
								<div
									className={`rounded-3xl border px-4 py-3 text-sm ${
										feedback.type === "success"
											? "border-app-green/30 bg-app-green/12 text-white"
											: "border-app-danger/30 bg-app-danger/12 text-white"
									}`}
								>
									{feedback.message}
								</div>
							) : null}

							<button
								type="submit"
								disabled={isSubmitting}
								className="inline-flex items-center justify-center gap-3 rounded-full bg-[linear-gradient(135deg,var(--color-app-accent),var(--color-app-accent-secondary))] px-6 py-3.5 text-sm font-semibold text-white shadow-[0_22px_40px_-24px_rgba(255,90,95,0.72)] transition disabled:cursor-not-allowed disabled:opacity-60"
							>
								{isSubmitting ? "Envoi en cours..." : "Envoyer le message"}
							</button>
						</form>
					</section>
				</section>
			</DocumentPageShell>
		</>
	);
}
